import React, { useState, useEffect } from 'react';
import { X, Save, Mail, Building2, Phone, MapPin, Lock, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Phone number formatting function - formats as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const phoneNumberDigits = phoneNumber.slice(0, 10);
  
  // Format based on length
  if (phoneNumberDigits.length === 0) {
    return '';
  } else if (phoneNumberDigits.length <= 3) {
    return `(${phoneNumberDigits}`;
  } else if (phoneNumberDigits.length <= 6) {
    return `(${phoneNumberDigits.slice(0, 3)}) ${phoneNumberDigits.slice(3)}`;
  } else {
    return `(${phoneNumberDigits.slice(0, 3)}) ${phoneNumberDigits.slice(3, 6)}-${phoneNumberDigits.slice(6)}`;
  }
};

// Extract digits from formatted phone number
const getPhoneDigits = (formattedPhone) => {
  return formattedPhone.replace(/\D/g, '');
};

const UserProfile = ({ onBack, logout }) => {
  const { currentUser, updateUserEmail } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [profile, setProfile] = useState({
    email: '',
    businessName: '',
    phoneNumber: '',
    address: ''
  });

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;

      setLoading(true);
      try {
        // Load from localStorage first (for offline support)
        const localProfile = localStorage.getItem(`userProfile_${currentUser.uid}`);
        if (localProfile) {
          const parsed = JSON.parse(localProfile);
          // Format phone number if it exists (it's stored as digits)
          const formattedPhone = parsed.phoneNumber ? formatPhoneNumber(parsed.phoneNumber) : '';
          setProfile({
            email: currentUser.email || '',
            businessName: parsed.businessName || '',
            phoneNumber: formattedPhone,
            address: parsed.address || ''
          });
        }

        // Then try to load from Firestore
        const profileRef = doc(db, 'users', currentUser.uid, 'profile', 'data');
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          // Format phone number if it exists (it's stored as digits)
          const formattedPhone = data.phoneNumber ? formatPhoneNumber(data.phoneNumber) : '';
          const updatedProfile = {
            email: currentUser.email || '',
            businessName: data.businessName || '',
            phoneNumber: formattedPhone,
            address: data.address || ''
          };
          setProfile(updatedProfile);
          setOriginalEmail(currentUser.email || '');
          // Update localStorage
          localStorage.setItem(`userProfile_${currentUser.uid}`, JSON.stringify({
            businessName: updatedProfile.businessName,
            phoneNumber: data.phoneNumber || '', // Store digits in localStorage
            address: updatedProfile.address
          }));
        } else {
          // No profile exists yet, initialize with email
          setProfile({
            email: currentUser.email || '',
            businessName: '',
            phoneNumber: '',
            address: ''
          });
          setOriginalEmail(currentUser.email || '');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        // Still set email from currentUser even if Firestore fails
        setProfile({
          email: currentUser.email || '',
          businessName: '',
          phoneNumber: '',
          address: ''
        });
        setOriginalEmail(currentUser.email || '');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleChange = (field, value) => {
    if (field === 'phoneNumber') {
      // Format phone number as user types
      const formatted = formatPhoneNumber(value);
      setProfile(prev => ({
        ...prev,
        [field]: formatted
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;

    // Check if email has changed
    const emailChanged = profile.email.trim() !== originalEmail.trim() && profile.email.trim() !== '';
    
    if (emailChanged) {
      // Show password modal for re-authentication
      setShowPasswordModal(true);
      return;
    }

    // Proceed with saving profile (no email change)
    await saveProfile();
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    setPasswordError('');
    setSaving(true);

    try {
      // Update email with re-authentication
      await updateUserEmail(profile.email.trim(), password);
      
      // Close modal and save profile
      setShowPasswordModal(false);
      setPassword('');
      await saveProfile();
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/requires-recent-login') {
        setPasswordError('For security, please sign out and sign back in, then try again.');
      } else if (error.code === 'auth/wrong-password') {
        setPasswordError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setPasswordError('Invalid email address format.');
      } else if (error.code === 'auth/email-already-in-use') {
        setPasswordError('This email is already in use by another account.');
      } else {
        setPasswordError(error.message || 'Failed to update email. Please try again.');
      }
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Get phone digits for storage (remove formatting)
      const phoneDigits = getPhoneDigits(profile.phoneNumber);
      
      // Validate phone number if provided (must be 10 digits)
      if (phoneDigits && phoneDigits.length > 0 && phoneDigits.length !== 10) {
        alert('Please enter a valid 10-digit phone number.');
        setSaving(false);
        return;
      }

      const profileData = {
        email: profile.email.trim(),
        businessName: profile.businessName.trim(),
        phoneNumber: phoneDigits, // Store digits only
        address: profile.address.trim(),
        updatedAt: serverTimestamp()
      };

      // Save to localStorage immediately
      localStorage.setItem(`userProfile_${currentUser.uid}`, JSON.stringify({
        businessName: profileData.businessName,
        phoneNumber: profileData.phoneNumber,
        address: profileData.address
      }));

      // Save to Firestore
      const profileRef = doc(db, 'users', currentUser.uid, 'profile', 'data');
      await setDoc(profileRef, profileData, { merge: true });

      console.log('✅ User profile saved');
      
      // Update original email if it was changed
      if (profile.email.trim() !== originalEmail) {
        setOriginalEmail(profile.email.trim());
      }
      
      // Dispatch event to notify other components of profile update
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: {
          businessName: profileData.businessName,
          phoneNumber: profileData.phoneNumber,
          address: profileData.address,
          email: profileData.email
        }
      }));
      
      onBack();
    } catch (error) {
      console.error('Error saving user profile:', error);
      alert('Error saving profile: ' + (error.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-primary">Loading profile...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-primary">
        <div className="header shadow-lg border-b border-primary sticky top-0 z-10 relative">
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={onBack} 
              className="flex items-center text-accent-primary hover:text-accent-hover touchable"
            >
              <X className="w-5 h-5 mr-2" />
              Back
            </button>
            <div className="w-16"></div>
          </div>
          <h1 className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xl font-bold text-primary m-0">
            Account Information
          </h1>
        </div>

        <div className="main-content p-4 md:p-6">
          <div className="card max-w-2xl mx-auto">
            <div className="space-y-6">
              {/* Email Address */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="form-input w-full focusable"
                  placeholder="Email address"
                />
                <p className="text-xs text-tertiary mt-1">
                  Changing your email requires password verification for security
                </p>
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Business Name
                </label>
                <input
                  type="text"
                  value={profile.businessName}
                  onChange={(e) => handleChange('businessName', e.target.value)}
                  className="form-input w-full focusable"
                  placeholder="Enter your business name"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profile.phoneNumber}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  className="form-input w-full focusable"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
                <p className="text-xs text-tertiary mt-1">
                  Enter 10-digit phone number
                </p>
              </div>

              {/* Physical Address */}
              <div>
                <label className="block text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Physical Address
                </label>
                <textarea
                  value={profile.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="form-input w-full focusable min-h-[100px] resize-y"
                  placeholder="Street Address, City, State, ZIP"
                  rows={3}
                />
              </div>

              {/* Logout Button */}
              <div className="pt-4 border-t border-primary">
                <button
                  onClick={logout}
                  className="btn btn-danger w-full touchable flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-primary">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary w-full touchable"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2 inline" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal for Email Change */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-5 h-5 text-accent-primary" />
              <h3 className="text-xl font-bold text-primary">Verify Password</h3>
            </div>
            <p className="text-tertiary mb-4">
              For security, please enter your current password to change your email address.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className="form-input w-full focusable"
                  placeholder="Enter your password"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePasswordSubmit();
                    }
                  }}
                />
                {passwordError && (
                  <p className="text-danger text-sm mt-1">{passwordError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setPasswordError('');
                    // Revert email to original
                    setProfile(prev => ({ ...prev, email: originalEmail }));
                  }}
                  className="btn btn-secondary flex-1 touchable"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  disabled={saving || !password}
                  className="btn btn-primary flex-1 touchable"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Verifying...
                    </>
                  ) : (
                    'Verify & Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfile;
